import {
	LayoutTagGetResponse,
	LayoutTagGetResponse_CustomHtmlCss,
	LayoutTagGetResponse_GeneralSetting,
	LayoutTagGetResponse_WordSetting,
} from "@/apiRequests/models/communityTalk/layoutTagGetResponse";
import { ReviewerAttributeDto } from "@/apiRequests/models/communityTalk/talkRoomGetResponse";
import { ReviewerAttributeAnswerPostRequest } from "@/apiRequests/models/communityTalk/reviewerAttributeAnswerPostRequest";
import TalkRoomController from "@/controllers/communityTalk/talkRoomController";
import AppConst from "@/modules/const";
import LoadTagDataUtil from "@/modules/utils/loadTagDataUtil";
import LocalStorageUtil from "@/modules/utils/localStorageUtil";
import ReviCoCookieUtil from "@/modules/utils/reviCoCookieUtil";
import { CommunityTalkUserProfilePicViewModel } from "@/viewModels/communityTalk/communityTalkUserProfilePicViewModel";
import { CommunityTalkUserReviewerAttributeViewModel } from "@/viewModels/communityTalk/communityTalkUserReviewerAttributeViewModel";
import { reactive } from "vue";
import { PostMediaViewModel } from "@/viewModels/communityTalk/postMediaViewModel";
import { PostPermission } from "@/modules/types/postPermissionType";
/**
 * コミュニティトーク>トークルームタグ1つ毎に1つ所持されるStore。
 */
interface ICommunityTalkState {
	_targetElement: HTMLElement | undefined;
	_tenantId: string | undefined;
	_siteUserId: string | undefined;
	_tagCd: string | undefined;
	_subjectCd: string | undefined;
	_subjectName: string | undefined;
	_ReviCoCookieId: string | undefined;
	_layoutTagData: LayoutTagGetResponse | undefined;
	_talkRoomUrl: string | undefined;
	_communityTalkUserNickName: string;
	_communityTalkUserReviewerAttributes: CommunityTalkUserReviewerAttributeViewModel[];
	_postableReviewerAttributes: ReviewerAttributeDto[];
	_communityTalkUserProfilePicViewModel: CommunityTalkUserProfilePicViewModel;
	_postMediaViewModel: PostMediaViewModel[];
	_displayTalkRoomId: string | undefined;
}
interface IUseCommunityTalkStore {
	/**
	 * マウント対象のHTML。
	 */
	getTargetElement: () => HTMLElement | undefined;
	/**
	 * マウント対象のHTML: セット。
	 */
	setTargetElement: (targetElement: HTMLElement | undefined) => void;
	// #region 貼り付けタグで読み取る情報
	/**
	 * 貼り付けタグで読み取る情報: テナントID。
	 */
	getTenantId: () => string | undefined;
	/**
	 * 貼り付けタグで読み取る情報: サイトのユーザーID。
	 */
	getSiteUserId: () => string | undefined;
	/**
	 * 貼り付けタグで読み取る情報: コミュニティトークのタグコード。
	 */
	getTagCd: () => string | undefined;
	/**
	 * 議題コード。
	 */
	getSubjectCd: () => string | undefined;
	/**
	 * 議題名。
	 */
	getSubjectName: () => string | undefined;
	/**
	 * 表示用トークルームID。
	 */
	getDisplayTalkRoomId: () => string | undefined;
	// #endregion
	/**
	 * ReviCoCookieId。
	 */
	getReviCoCookieId: () => string | undefined;
	// #region 表示タグ情報
	/**
	 * 表示タグ情報。
	 */
	getLayoutTagData: () => LayoutTagGetResponse;
	/**
	 * 表示タグ設定>基本設定。
	 */
	getGeneralSettings: () => LayoutTagGetResponse_GeneralSetting;
	/**
	 * 表示タグ情報>文言設定。
	 */
	getWordSettings: () => LayoutTagGetResponse_WordSetting;
	/**
	 * 表示タグ情報>カスタムHTML&CSS。
	 */
	getCustomHtmlCss: () => LayoutTagGetResponse_CustomHtmlCss;
	// #endregion
	/**
	 * トークルーム表示URL
	 */
	getTalkRoomUrl: () => string | undefined;
	// #region ユーザー情報
	/**
	 * ユーザー>ニックネーム。
	 */
	getCommunityTalkUserNickName: () => string;
	/**
	 * ユーザー>ニックネーム: セット。
	 */
	setCommunityTalkUserNickName: (nickname: string) => void;
	/**
	 * ユーザー>投稿者プロフィール。
	 * 投稿可能な投稿者プロフィールに絞って返却。
	 */
	getCommunityTalkUserReviewerAttributes: () => ReviewerAttributeAnswerPostRequest[];
	/**
	 * 投稿可能な投稿者プロフィール。
	 */
	getPostableReviewerAttributes: () => ReviewerAttributeDto[];
	/**
	 * 投稿可能な投稿者プロフィール: セット。
	 */
	setPostableReviewerAttributes: (value: ReviewerAttributeDto[]) => void;
	/**
	 * ユーザー>投稿者プロフィール: セット。
	 */
	setCommunityTalkUserReviewerAttributes: (reviewerAttributeAnswers: ReviewerAttributeAnswerPostRequest[]) => void;
	/**
	 * ユーザー>プロフィール画像情報。
	 */
	getCommunityTalkUserProfilePicViewModel: () => CommunityTalkUserProfilePicViewModel;
	/**
	 * ユーザー>プロフィール画像情報: セット。
	 */
	setCommunityTalkUserProfilePicViewModel: (viewModel: CommunityTalkUserProfilePicViewModel) => void;
	// #endregion
	// #region 投稿画像・動画情報
	/**
	 * トークルーム>投稿画像・動画情報: 設定。
	 */
	setTalkRoomPostMedia: (data: PostMediaViewModel[]) => void;
	/**
	 * トークルーム>投稿画像・動画情報: 全削除。
	 */
	resetTalkRoomPostMedia: () => void;
	/**
	 * トークルーム>投稿画像・動画情報: 取得。
	 */
	getTalkRoomPostMedia: () => PostMediaViewModel[];
	// #endregion

	/**
	 * 初期化処理。
	 * このStoreが親コンポーネントから初期作成された際に実行すること。
	 * 表示タグ情報の読み込みもここで行う。
	 * @returns 表示タグ情報の読み込みAPIステータス
	 */
	initAndGetLayoutTagData: (targetElement: HTMLElement | undefined) => Promise<void>;
	/**
	 * ユーザー情報をLocalStorageにセットする。
	 */
	setCommunityTalkViewModelToLocalStorage: () => void;
}
/**
 * 貼り付けタグで読み取る情報の状態管理。
 */
export const useCommunityTalkStore = (): IUseCommunityTalkStore => {
	// 状態
	const state = reactive<ICommunityTalkState>({
		_targetElement: undefined,
		_tenantId: undefined,
		_siteUserId: undefined,
		_tagCd: undefined,
		_subjectCd: undefined,
		_subjectName: undefined,
		_ReviCoCookieId: undefined,
		_layoutTagData: undefined,
		_talkRoomUrl: undefined,
		_communityTalkUserNickName: "",
		_communityTalkUserReviewerAttributes: [],
		_postableReviewerAttributes: [],
		_communityTalkUserProfilePicViewModel: {
			profilePicIdTmp: "",
			profilePicUrl: "",
		},
		_postMediaViewModel: [],
		_displayTalkRoomId: undefined,
	});
	const getTargetElement = (): HTMLElement | undefined => {
		return state._targetElement;
	};
	const setTargetElement = (targetElement: HTMLElement | undefined) => {
		// HTMLElementがundefinedの場合はエラーを発生
		if (!targetElement) {
			console.error("revico community talk store init error: targetElement undefined.");
		}
		state._targetElement = targetElement;
	};
	// #region 貼り付けタグで読み取る情報
	const getTenantId = (): string | undefined => {
		return state._tenantId;
	};
	const getSiteUserId = (): string | undefined => {
		return state._siteUserId;
	};
	const getTagCd = (): string | undefined => {
		return state._tagCd;
	};
	const getSubjectCd = (): string | undefined => {
		return state._subjectCd;
	};
	const getSubjectName = (): string | undefined => {
		return state._subjectName;
	};
	const getDisplayTalkRoomId = (): string | undefined => {
		return state._displayTalkRoomId;
	};
	// #endregion
	const getReviCoCookieId = (): string | undefined => {
		// 処理順の関係でCookieIdが取得できてない可能性があるため、CookieIdが無ければもう一度トライする
		if (!state._ReviCoCookieId) {
			state._ReviCoCookieId = ReviCoCookieUtil.getReviCoCookieId();
		}
		return state._ReviCoCookieId;
	};
	// #region 表示タグ情報
	const getLayoutTagData = (): LayoutTagGetResponse => {
		if (!state._layoutTagData) {
			throw new Error("layoutTagData is undefined.");
		}
		return state._layoutTagData;
	};
	const getGeneralSettings = (): LayoutTagGetResponse_GeneralSetting => {
		if (!state._layoutTagData) {
			throw new Error("layoutTagData is undefined.");
		}
		const generalSettings = state._layoutTagData.generalSettings;
		return {
			...generalSettings,
			nicePostPermissionSetting: generalSettings.nicePostPermissionSetting ?? PostPermission.all, // デフォルト値を設定
		};
	};
	const getWordSettings = (): LayoutTagGetResponse_WordSetting => {
		if (!state._layoutTagData) {
			throw new Error("layoutTagData is undefined.");
		}
		return state._layoutTagData.wordSettings;
	};
	const getCustomHtmlCss = (): LayoutTagGetResponse_CustomHtmlCss => {
		if (!state._layoutTagData) {
			throw new Error("layoutTagData is undefined.");
		}
		return state._layoutTagData.customHtmlCss;
	};
	// #endregion
	const getTalkRoomUrl = (): string | undefined => {
		return state._talkRoomUrl;
	};
	// #region ユーザー情報
	const getCommunityTalkUserNickName = () => {
		return state._communityTalkUserNickName;
	};
	const getCommunityTalkUserReviewerAttributes = () => {
		// 対象テナントのデータを返却
		const index = state._communityTalkUserReviewerAttributes.findIndex((e) => e.tenantId === state._tenantId);
		if (index > -1) {
			return state._communityTalkUserReviewerAttributes[index].datas.filter((x) => {
				const isPostable = state._postableReviewerAttributes.filter((y) => x.ReviewerAttributeId === y.reviewerAttributeId);
				if (isPostable.length > 0) {
					return true;
				}
			});
		}
		return [];
	};
	const getPostableReviewerAttributes = (): ReviewerAttributeDto[] => {
		return state._postableReviewerAttributes;
	};
	const setPostableReviewerAttributes = (value: ReviewerAttributeDto[]): void => {
		state._postableReviewerAttributes = value;
	};
	const setCommunityTalkUserNickName = (nickname: string) => {
		state._communityTalkUserNickName = nickname;
	};
	const setCommunityTalkUserReviewerAttributes = (reviewerAttributeAnswers: ReviewerAttributeAnswerPostRequest[]) => {
		// 対象テナントの投稿者プロフィール情報として登録する
		const index = state._communityTalkUserReviewerAttributes.findIndex((e) => e.tenantId === state._tenantId);
		if (index > -1) {
			state._communityTalkUserReviewerAttributes[index].datas = reviewerAttributeAnswers;
		} else {
			// 対象テナントのデータが存在しなければ追加
			state._communityTalkUserReviewerAttributes.push({
				tenantId: state._tenantId as string,
				datas: reviewerAttributeAnswers,
			});
		}
	};
	const getCommunityTalkUserProfilePicViewModel = () => {
		return state._communityTalkUserProfilePicViewModel;
	};
	const setCommunityTalkUserProfilePicViewModel = (viewModel: CommunityTalkUserProfilePicViewModel) => {
		state._communityTalkUserProfilePicViewModel = viewModel;
	};
	// #endregion
	// #region 投稿画像・動画情報
	const setTalkRoomPostMedia = (data: PostMediaViewModel[]): void => {
		state._postMediaViewModel = data;
	};
	const resetTalkRoomPostMedia = (): void => {
		state._postMediaViewModel = [];
	};
	const getTalkRoomPostMedia = (): PostMediaViewModel[] => {
		return state._postMediaViewModel;
	};
	// #endregion

	// PBI50364: 過去のLocalStorage構造を新しいLocalStorage構造へ一新する
	const convertLocalStoragePastFormat = (): void => {
		// nickName
		const replyUserNickname_localStorage = LocalStorageUtil.loadLocalStorage<string>(AppConst.LOCALSTORAGE_KEYS.REPLY_POST_NICKNAME);
		if (replyUserNickname_localStorage) {
			LocalStorageUtil.saveLocalStorage(AppConst.LOCALSTORAGE_KEYS.COMMUNITY_TALK_USER_NICKNAME, replyUserNickname_localStorage);
		}
		// reviewerAttributes
		const replyUserReviewerAttributes_localStorage = LocalStorageUtil.loadLocalStorage<CommunityTalkUserReviewerAttributeViewModel[]>(AppConst.LOCALSTORAGE_KEYS.REPLY_POST_REVIEWER_ATTRIBUTES);
		if (replyUserReviewerAttributes_localStorage) {
			LocalStorageUtil.saveLocalStorage(AppConst.LOCALSTORAGE_KEYS.COMMUNITY_TALK_USER_REVIEWER_ATTRIBUTES, replyUserReviewerAttributes_localStorage);
		}
		// profilePic
		const replyUserProfilePicViewModel_localStorage = LocalStorageUtil.loadLocalStorage<CommunityTalkUserProfilePicViewModel>(AppConst.LOCALSTORAGE_KEYS.REPLY_POST_PROFILE_PICTURE);
		if (replyUserProfilePicViewModel_localStorage) {
			LocalStorageUtil.saveLocalStorage(AppConst.LOCALSTORAGE_KEYS.COMMUNITY_TALK_USER_PROFILE_PICTURE, replyUserProfilePicViewModel_localStorage);
		}
		// 古いLocalStorageを削除
		LocalStorageUtil.removeLocalStorage(AppConst.LOCALSTORAGE_KEYS.REPLY_POST_NICKNAME);
		LocalStorageUtil.removeLocalStorage(AppConst.LOCALSTORAGE_KEYS.REPLY_POST_REVIEWER_ATTRIBUTES);
		LocalStorageUtil.removeLocalStorage(AppConst.LOCALSTORAGE_KEYS.REPLY_POST_PROFILE_PICTURE);
	};
	const initAndGetLayoutTagData = async (targetElement: HTMLElement | undefined): Promise<void> => {
		// マウント対象のHTMLをセット
		setTargetElement(targetElement);

		// 貼り付けタグに仕込まれたデータを取得:
		// テナントIDを貼り付けタグから取得
		state._tenantId = LoadTagDataUtil.loadTenantId(state._targetElement);
		// コミュニティトークのタグコードを貼り付けタグから取得
		state._tagCd = LoadTagDataUtil.loadTagCd(state._targetElement);

		// 表示用トークルームIDを貼り付けタグから取得
		state._displayTalkRoomId = LoadTagDataUtil.loadDisplayTalkRoomId(AppConst.DISPLAY_TALK_ROOM_ID_HIDDEN_ID);

		// ReviCoCookieId取得
		state._ReviCoCookieId = ReviCoCookieUtil.getReviCoCookieId();

		// 表示タグ情報を取得
		const talkRoomController = new TalkRoomController();
		try {
			state._layoutTagData = await talkRoomController.getLayoutTagData(state._tenantId, state._tagCd);

			// タグ情報から取得した埋め込みHTML IDでStoreの値を更新
			state._subjectCd = LoadTagDataUtil.loadSubjectCd(state._layoutTagData?.generalSettings.targetSubjectCdElementId);
			state._subjectName = LoadTagDataUtil.loadSubjectName(AppConst.SUBJECT_NAME_HIDDEN_ID);
			state._siteUserId = LoadTagDataUtil.loadSiteUserId(state._layoutTagData?.generalSettings.targetCustomerCdElementId);
			state._talkRoomUrl = LoadTagDataUtil.loadTalkRoomUrl(state._layoutTagData?.generalSettings.targetTalkRoomUrlElementId);
			const presetCommunityTalkUserNickName = LoadTagDataUtil.loadCommunityTalkUserNickName(state._layoutTagData?.generalSettings.targetCommunityTalkUserNickNameElementId);
			state._communityTalkUserNickName = presetCommunityTalkUserNickName ? presetCommunityTalkUserNickName.substring(0, AppConst.COMMUNITY_TALK_USER_NICKNAME_MAX_LENGTH) : "";

			// TODO:一時的に非会員リストを固定値でベタ書きする
			state._layoutTagData.generalSettings.nonMemberIdList = ["00000000-0000-0000-0000-000000000000"];
		} catch (e) {
			console.error("init at Error! : ErrorContents:");
			console.error(e);
			throw new Error();
		}

		// LocalStorageから、投稿時のユーザープリセットデータを復元
		convertLocalStoragePastFormat();
		const communityTalkUserNickname_localStorage = LocalStorageUtil.loadLocalStorage<string>(AppConst.LOCALSTORAGE_KEYS.COMMUNITY_TALK_USER_NICKNAME);
		if (communityTalkUserNickname_localStorage) {
			state._communityTalkUserNickName = communityTalkUserNickname_localStorage;
		} else {
			const replyUserNickname_localStorage = LocalStorageUtil.loadLocalStorage<string>(AppConst.LOCALSTORAGE_KEYS.REPLY_POST_NICKNAME);
			if (replyUserNickname_localStorage) {
				state._communityTalkUserNickName = replyUserNickname_localStorage;
			}
		}
		const communityTalkUserReviewerAttributes_localStorage = LocalStorageUtil.loadLocalStorage<CommunityTalkUserReviewerAttributeViewModel[]>(
			AppConst.LOCALSTORAGE_KEYS.COMMUNITY_TALK_USER_REVIEWER_ATTRIBUTES
		);
		if (communityTalkUserReviewerAttributes_localStorage) {
			state._communityTalkUserReviewerAttributes = communityTalkUserReviewerAttributes_localStorage;
		} else {
			const replyUserReviewerAttributes_localStorage = LocalStorageUtil.loadLocalStorage<CommunityTalkUserReviewerAttributeViewModel[]>(
				AppConst.LOCALSTORAGE_KEYS.REPLY_POST_REVIEWER_ATTRIBUTES
			);
			if (replyUserReviewerAttributes_localStorage) {
				state._communityTalkUserReviewerAttributes = replyUserReviewerAttributes_localStorage;
			}
		}
		const communityTalkUserProfilePicViewModel_localStorage = LocalStorageUtil.loadLocalStorage<CommunityTalkUserProfilePicViewModel>(
			AppConst.LOCALSTORAGE_KEYS.COMMUNITY_TALK_USER_PROFILE_PICTURE
		);
		if (communityTalkUserProfilePicViewModel_localStorage) {
			state._communityTalkUserProfilePicViewModel = {
				profilePicIdTmp: communityTalkUserProfilePicViewModel_localStorage.profilePicIdTmp,
				profilePicUrl: communityTalkUserProfilePicViewModel_localStorage.profilePicUrl,
			};
		} else {
			const replyUserProfilePicViewModel_localStorage = LocalStorageUtil.loadLocalStorage<CommunityTalkUserProfilePicViewModel>(AppConst.LOCALSTORAGE_KEYS.REPLY_POST_PROFILE_PICTURE);
			if (replyUserProfilePicViewModel_localStorage) {
				state._communityTalkUserProfilePicViewModel = {
					profilePicIdTmp: replyUserProfilePicViewModel_localStorage.profilePicIdTmp,
					profilePicUrl: replyUserProfilePicViewModel_localStorage.profilePicUrl,
				};
			}
		}
	};

	const setCommunityTalkViewModelToLocalStorage = (): void => {
		LocalStorageUtil.saveLocalStorage(AppConst.LOCALSTORAGE_KEYS.COMMUNITY_TALK_USER_NICKNAME, state._communityTalkUserNickName);
		LocalStorageUtil.saveLocalStorage(AppConst.LOCALSTORAGE_KEYS.COMMUNITY_TALK_USER_REVIEWER_ATTRIBUTES, state._communityTalkUserReviewerAttributes);
		LocalStorageUtil.saveLocalStorage(AppConst.LOCALSTORAGE_KEYS.COMMUNITY_TALK_USER_PROFILE_PICTURE, state._communityTalkUserProfilePicViewModel);
	};
	return {
		getTargetElement,
		setTargetElement,
		// #region 貼り付けタグで読み取る情報
		getTenantId,
		getSiteUserId,
		getTagCd,
		getSubjectCd,
		getSubjectName,
		getDisplayTalkRoomId,
		// #endregion
		getReviCoCookieId,
		// #region 表示タグ情報
		getLayoutTagData,
		getGeneralSettings,
		getWordSettings,
		getCustomHtmlCss,
		// #endregion
		getTalkRoomUrl,
		// #region ユーザー情報
		getCommunityTalkUserNickName,
		getCommunityTalkUserReviewerAttributes,
		getPostableReviewerAttributes,
		setPostableReviewerAttributes,
		setCommunityTalkUserNickName,
		setCommunityTalkUserReviewerAttributes,
		getCommunityTalkUserProfilePicViewModel,
		setCommunityTalkUserProfilePicViewModel,
		// #endregion
		// #region 投稿画像・動画情報
		setTalkRoomPostMedia,
		resetTalkRoomPostMedia,
		getTalkRoomPostMedia,
		// #endregion
		initAndGetLayoutTagData,
		setCommunityTalkViewModelToLocalStorage,
	};
};
// ステートの型を登録し、外部でもIUseAnimationを参照し、型を効かせる
export type CommunityTalkStore = ReturnType<typeof useCommunityTalkStore>;

/**
 * 対象のシンボルキーが利用可能かどうかを示す。
 * @param key シンボルキー代わりの文字列。
 * @returns `true`:利用可能 / `false`:利用不可。
 */
export const isValidCommunityTalkStoreKey = (key: string): boolean => {
	// なんらかの原因でInjectKeyにエラーが発生していた場合はコンソール出力
	if (!key) {
		console.error(`Injection failed.`);
		return false;
	}
	return true;
};
